import React, { FC, ReactNode, useCallback, useRef, useState } from 'react';
import createCtx from 'utils/createCtx';
import { scrollIntoView } from 'utils/domUtils';
import { RegisterGuardAndScrollGuardFn, StepSubmitGuardFn, StepSubmitContextType } from './StepSubmitGuardCtx.type';

const [useStepSubmitContext, StepSubmitCtxProvider] = createCtx<StepSubmitContextType>('useStepSubmit');

type StepSubmitProviderProps = {
  children: (
    onBeforeSubmit: ((e: React.MouseEvent) => void | Promise<void>) | undefined,
    disableSubmit: boolean | undefined,
    formRef: React.RefObject<HTMLFormElement>,
    backBlocked: boolean,
    backBlockedTooltip: string | undefined,
  ) => ReactNode;
};

/**
 * Provider that gives form steps control over the submit button.
 * Wrap it around the form in `productFormContent` and attach `formRef` to the `<form>` element.
 */
export const StepSubmitProvider: FC<StepSubmitProviderProps> = ({ children }) => {
  const [guard, setGuardState] = useState<StepSubmitGuardFn | null>(null);
  const [disableSubmit, setDisableSubmitState] = useState(false);
  const [backBlocked, setBackBlockedState] = useState(false);
  const [backBlockedTooltip, setBackBlockedTooltipState] = useState<string | undefined>(undefined);
  const formRef = useRef<HTMLFormElement>(null);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const registerGuard = useCallback((fn: StepSubmitGuardFn | null) => {
    setGuardState(() => fn);
  }, []);

  const registerGuardAndScroll = useCallback(
    (selector: string | null, guard?: RegisterGuardAndScrollGuardFn) => {
      if (selector === null) {
        registerGuard(null);
        if (scrollTimerRef.current !== null) {
          clearTimeout(scrollTimerRef.current);
          scrollTimerRef.current = null;
        }
        return;
      }

      const scrollToTarget = () => {
        if (scrollTimerRef.current !== null) {
          clearTimeout(scrollTimerRef.current);
        }
        scrollTimerRef.current = setTimeout(() => {
          scrollIntoView(selector);
          scrollTimerRef.current = null;
        }, 50);
      };

      if (guard) {
        registerGuard(async ({ proceed }) => {
          await guard({ proceed, scrollToTarget });
        });
      } else {
        registerGuard(() => {
          scrollToTarget();
        });
      }
    },
    [registerGuard],
  );

  const setBlocked = useCallback((blocked: boolean) => {
    setDisableSubmitState(blocked);
  }, []);

  const setBackBlocked = useCallback((blocked: boolean, tooltipText?: string) => {
    setBackBlockedState(blocked);
    setBackBlockedTooltipState(blocked ? tooltipText : undefined);
  }, []);

  const triggerFormSubmit = useCallback(() => {
    const form = formRef.current;
    if (!form) return;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }, []);

  // Build the button onClick from the guard — steps never handle raw MouseEvent
  const onBeforeSubmit = guard
    ? async (e: React.MouseEvent) => {
        e.preventDefault();
        await guard({ proceed: triggerFormSubmit });
      }
    : undefined;

  return (
    <StepSubmitCtxProvider
      value={{
        registerGuard,
        registerGuardAndScroll,
        setBlocked,
        setBackBlocked,
        _triggerFormSubmit: triggerFormSubmit,
      }}
    >
      {children(onBeforeSubmit, disableSubmit || undefined, formRef, backBlocked, backBlockedTooltip)}
    </StepSubmitCtxProvider>
  );
};

/**
 * Hook for form steps that need to control the submit button.
 *
 * @example Scroll to error on submit attempt (sync block):
 *   const { registerGuard } = useStepSubmit();
 *   useEffect(() => {
 *     if (!isBlocked) { registerGuard(null); return; }
 *     registerGuard(() => { scrollIntoView('#my-alert'); });
 *     return () => registerGuard(null);
 *   }, [isBlocked, registerGuard]);
 *
 * @example Async check before allowing submit:
 *   const { registerGuard, setBlocked } = useStepSubmit();
 *   useEffect(() => {
 *     registerGuard(async ({ proceed }) => {
 *       const ok = await checkEligibility();
 *       if (!ok) { setBlocked(true); return; }
 *       proceed(); // triggers form submit
 *     });
 *     return () => { registerGuard(null); setBlocked(false); };
 *   }, [registerGuard, setBlocked]);
 * @example Scroll to error on submit attempt (with async check):
 *  const { registerGuardAndScroll } = useStepSubmit();
 *   useEffect(() => {
 *    if (!isBlocked) { registerGuardAndScroll(null); return; }
 *    registerGuardAndScroll('#my-alert');
 *    return () => registerGuardAndScroll(null);
 *   }, [isBlocked, registerGuardAndScroll]);
 */
export const useStepSubmit = () => {
  const { registerGuard, registerGuardAndScroll, setBlocked, setBackBlocked } = useStepSubmitContext();
  return { registerGuard, registerGuardAndScroll, setBlocked, setBackBlocked };
};
